import React from 'react';
import { useAppContext } from '../../AppContext';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

const getProfileThemes = (t) => ({
    default: { name: t('profileCustomization.themes.default'), previewClass: 'bg-gray-800' },
    starlight: { name: t('profileCustomization.themes.starlight'), previewClass: 'bg-gradient-to-br from-indigo-900 to-purple-900', requiredBadge: 'elder_star' },
    sunrise: { name: t('profileCustomization.themes.sunrise'), previewClass: 'bg-gradient-to-br from-amber-500 to-red-700', requiredBadge: 'echo_deity' },
    verdant: { name: t('profileCustomization.themes.verdant'), previewClass: 'bg-gradient-to-br from-green-700 to-teal-900', requiredBadge: 'community_pillar' },
    abyssal: { name: t('profileCustomization.themes.abyssal'), previewClass: 'bg-gradient-to-br from-cyan-900 to-blue-900', requiredBadge: 'harmony_sage' },
    celestial: { name: t('profileCustomization.themes.celestial'), previewClass: 'bg-gradient-to-br from-yellow-100 to-blue-200', requiredBadge: 'elder_star' },
    glitch: { name: t('profileCustomization.themes.glitch'), previewClass: 'bg-black', requiredBadge: 'bug_hunter' },
    holographic: { name: t('profileCustomization.themes.holographic'), previewClass: 'bg-gradient-to-br from-purple-500 to-cyan-500', requiredBadge: 'echo_magnate' },
    neonnoir: { name: t('profileCustomization.themes.neonnoir'), previewClass: 'bg-gradient-to-br from-blue-900 to-purple-900', requiredBadge: 'archivist' },
    synthwave: { name: t('profileCustomization.themes.synthwave'), previewClass: 'bg-gradient-to-br from-pink-500 to-purple-600', requiredBadge: 'trendsetter' },
    moltencore: { name: t('profileCustomization.themes.moltencore'), previewClass: 'bg-gradient-to-br from-red-800 to-yellow-600', requiredBadge: 'prolific_commenter' },
    aperture: { name: t('profileCustomization.themes.aperture'), previewClass: 'bg-gray-200', requiredBadge: 'networker' },
    foundersorb: { name: t('profileCustomization.themes.foundersorb'), previewClass: 'bg-gradient-to-br from-gray-700 via-gray-900 to-black', requiresFounder: true },
    presignup: { name: t('profileCustomization.themes.presignup'), previewClass: 'bg-gradient-to-br from-rose-900 to-indigo-900', requiresFounder: true },
});


const ThemeView = () => {
    const { t } = useTranslation();
    const { currentUserProfile, updateUserProfile, updateUserProfileInState, setMessage } = useAppContext();
    const selectedTheme = currentUserProfile?.profileTheme || 'default';
    const userBadges = new Set(currentUserProfile?.badges || []);
    const profileThemes = getProfileThemes(t);

    const handleThemeSelect = (themeId) => {
        if (currentUserProfile) {
            updateUserProfile(currentUserProfile.id, { profileTheme: themeId })
                .then(() => {
                    updateUserProfileInState(currentUserProfile.id, { profileTheme: themeId });
                    setMessage(t('profileCustomization.themeUpdateSuccess'));
                })
                .catch(error => {
                    setMessage(t('profileCustomization.themeUpdateError'));
                });
        }
    };

    return (
        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50">
            <h2 className="text-3xl font-bold font-playfair mb-2 text-pink-300">App Theme</h2>
            <p className="mb-6 text-gray-400">Personalize the look and feel of your universe.</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(profileThemes).map(([id, theme]) => {
                    const isSelected = selectedTheme === id;
                    const hasBadge = !theme.requiredBadge || userBadges.has(theme.requiredBadge);
                    const isFounder = theme.requiresFounder ? currentUserProfile.isFounder : true;
                    const isUnlocked = hasBadge && isFounder;

                    return (
                        <div
                            key={id}
                            onClick={() => isUnlocked && handleThemeSelect(id)}
                            className={`relative cursor-pointer border-2 rounded-lg p-2 transition-all transform hover:scale-105 ${
                                isSelected ? 'border-pink-400 shadow-glow-pink' : 'border-gray-700'
                            } ${isUnlocked ? 'hover:border-pink-500' : 'opacity-50 cursor-not-allowed'}`}
                        >
                            <div className={`w-full h-24 rounded-md ${theme.previewClass}`}></div>
                            <p className="text-center mt-2 font-semibold">{theme.name}</p>
                            {!isUnlocked && (
                                <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded-lg">
                                    <Lock size={24} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export default ThemeView;